import { toast } from 'sonner'
import {
  fcmTokenFingerprint,
  getFcmToken,
  isFcmConfigured,
  regenerateFcmToken,
} from '@/services/fcm'
import { fcmTokenService } from '@/modules/notifications/services/fcm-token.service'

/** Last successful backend sync for this tab — skips duplicate register spam. */
let lastSynced: { userId: string; token: string } | null = null

/** Tokens this tab already learned are dead — never re-POST; mint a new one first. */
const knownDeadTokens = new Set<string>()

/**
 * Bumped ONLY on logout. Must NOT increment on every sync/focus — that aborted
 * in-flight getToken→POST and left the API with no register logs.
 */
let logoutGeneration = 0

let syncInFlight: Promise<void> | null = null
let syncInFlightUserId: string | null = null

/**
 * Push-setup warnings are shown at most ONCE per browser session per problem.
 * Sync re-runs on focus / app switch, so without this the same toast kept
 * popping up every time the user came back to the tab. The console still logs
 * every failure for diagnosis.
 */
const WARNED_KEY = 'pmms.fcm.warned'
const warnedThisSession = new Set<string>()

function warnOnce(id: string, message: string): void {
  if (warnedThisSession.has(id)) return
  warnedThisSession.add(id)
  try {
    const seen = new Set<string>(JSON.parse(sessionStorage.getItem(WARNED_KEY) || '[]'))
    if (seen.has(id)) return
    seen.add(id)
    sessionStorage.setItem(WARNED_KEY, JSON.stringify([...seen]))
  } catch {
    // Storage blocked — the in-memory set still limits it to once per tab.
  }
  toast.warning(message)
}

function canUseFcmBrowser(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    isFcmConfigured()
  )
}

async function registerToken(token: string) {
  return fcmTokenService.register({
    token,
    platform: 'web',
    userAgent: navigator.userAgent,
  })
}

function toastGetTokenFailure(reason: string): void {
  const messages: Record<string, string> = {
    permission_denied:
      'Notifications are blocked for this site — Allow in the lock icon, then reload.',
    permission_dismissed: 'Notifications are not allowed yet — click Allow when prompted.',
    sw_failed: 'Push service worker failed to start. Hard reload, or clear site data for this origin.',
    token_failed: 'Could not get a push token. Hard reload this tab.',
    missing_config: 'Firebase push is not configured in this build.',
    unsupported: 'This browser does not support web push.',
  }
  warnOnce(`token:${reason}`, messages[reason] || `Desktop push failed (${reason}).`)
}

async function recoverDeadToken(
  userId: string,
  deadToken: string,
  startedAtLogoutGeneration: number,
): Promise<string | null> {
  knownDeadTokens.add(deadToken)
  if (lastSynced?.token === deadToken) lastSynced = null

  console.warn(
    `[fcm] server marked token dead (${fcmTokenFingerprint(deadToken)}); minting a new one`,
  )

  const fresh = await regenerateFcmToken()
  if (startedAtLogoutGeneration !== logoutGeneration) return null

  if (!fresh.ok) {
    toastGetTokenFailure(fresh.reason)
    return null
  }

  if (fresh.token === deadToken || knownDeadTokens.has(fresh.token)) {
    warnOnce(
      'token:stuck',
      'Desktop push token is stuck. Clear site data for this origin, Allow notifications, then reload.',
    )
    return null
  }

  const response = await registerToken(fresh.token)
  if (startedAtLogoutGeneration !== logoutGeneration) return null

  if (response.status === 'token_invalid') {
    knownDeadTokens.add(fresh.token)
    warnOnce('register:invalid', 'Desktop push could not register. Reload and allow notifications again.')
    return null
  }

  console.info(
    `[fcm] token replaced old=${fcmTokenFingerprint(deadToken)} new=${fcmTokenFingerprint(fresh.token)} user=${userId}`,
  )
  return fresh.token
}

/**
 * Register this browser's FCM token for the current user.
 * Pass `{ force: true }` on mount / focus so ownership is claimed after user switch.
 */
export async function syncFcmDeviceRegistration(
  userId: string,
  options?: { force?: boolean },
): Promise<void> {
  if (!userId || !canUseFcmBrowser()) return
  if (Notification.permission !== 'granted') {
    console.warn(`[fcm] skip register: permission=${Notification.permission}`)
    return
  }

  if (syncInFlight && syncInFlightUserId === userId) {
    await syncInFlight
    return
  }

  if (syncInFlight) {
    await syncInFlight.catch(() => {})
  }

  const force = Boolean(options?.force)
  const startedAtLogoutGeneration = logoutGeneration
  syncInFlightUserId = userId
  syncInFlight = (async () => {
    try {
      const result = await getFcmToken({ requestPermission: false })
      if (startedAtLogoutGeneration !== logoutGeneration) return
      if (!result.ok) {
        console.warn(`[fcm] getToken failed: ${result.reason}`)
        toastGetTokenFailure(result.reason)
        return
      }

      if (knownDeadTokens.has(result.token)) {
        const replaced = await recoverDeadToken(userId, result.token, startedAtLogoutGeneration)
        if (!replaced || startedAtLogoutGeneration !== logoutGeneration) return
        lastSynced = { userId, token: replaced }
        return
      }

      if (!force && lastSynced?.userId === userId && lastSynced.token === result.token) {
        return
      }

      let currentToken = result.token
      const response = await registerToken(currentToken)

      if (response.status === 'token_invalid' && response.regenerate) {
        if (startedAtLogoutGeneration !== logoutGeneration) return
        const replaced = await recoverDeadToken(userId, currentToken, startedAtLogoutGeneration)
        if (!replaced || startedAtLogoutGeneration !== logoutGeneration) return
        currentToken = replaced
        lastSynced = { userId, token: currentToken }
        return
      }

      if (startedAtLogoutGeneration !== logoutGeneration) return
      lastSynced = { userId, token: currentToken }
      console.info(`[fcm] token registered (${fcmTokenFingerprint(currentToken)})`)
    } catch (err) {
      console.warn('[fcm] token registration failed:', err instanceof Error ? err.message : err)
      warnOnce(
        'register:failed',
        'Desktop notifications could not be set up right now. They will retry automatically.',
      )
    }
  })().finally(() => {
    if (syncInFlightUserId === userId) {
      syncInFlight = null
      syncInFlightUserId = null
    }
  })

  await syncInFlight
}

/**
 * Explicit logout cleanup for THIS browser's token only.
 */
export async function cleanupFcmOnLogout(): Promise<void> {
  logoutGeneration += 1
  syncInFlight = null
  syncInFlightUserId = null

  const knownToken = lastSynced?.token ?? null
  lastSynced = null

  if (!canUseFcmBrowser()) return

  try {
    let token = knownToken
    if (!token && Notification.permission === 'granted') {
      const result = await getFcmToken({ requestPermission: false })
      if (result.ok) token = result.token
    }

    if (token) {
      try {
        await fcmTokenService.unregister(token)
      } catch {
        // Server unregister is best-effort.
      }
    }
  } catch {
    // Logout must always proceed.
  }
}
